import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import FormField from '../../core/components/FormField.tsx';
import SaveStatus from '../../core/components/SaveStatus.tsx';
import countFieldErrors from '../../core/utils/countFieldErrors.ts';
import StringField from '../../core/components/Fields/StringField.tsx';
import { useTranslation } from 'react-i18next';
import Page from '../../core/components/Page';
import Layout from '../../core/components/Layout.tsx';
import { useMutation, useQuery } from '@apollo/client';
import { GET_COMPANY_DETAILS, UPDATE_SAML_CONFIG } from '../queries.ts';
import { useToaster } from '../../core/components/Toast.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import SettingsNavigation from '../../core/components/SettingsNavigation.tsx';
import TextField from '../../core/components/Fields/TextField.tsx';

const CUSTOMER_SAML_FORM_DEFAULT_VALUES = {
    isRequired: false,
    domain: '',
    allowSelfRegistration: false,
    idpEntityId: '',
    idpSsoUrl: '',
    idpSloUrl: '',
    idpX509Cert: '',
    spEntityId: '',
    spNameIdFormat: '',
    attributeMapping: '',
    wantAssertionsSigned: false,
    wantMessageSigned: false,
};

const CustomerSamlPage = () => {
    const { t } = useTranslation('customer');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: CUSTOMER_SAML_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const { data } = useQuery(GET_COMPANY_DETAILS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        variables: {},
    });
    const samlConfig = data?.viewer?.customer?.samlConfig;

    useEffect(() => {
        if (samlConfig) {
            form.reset({
                ...CUSTOMER_SAML_FORM_DEFAULT_VALUES,
                isRequired: samlConfig?.isRequired || false,
                domain: samlConfig?.domain || '',
                allowSelfRegistration: samlConfig?.allowSelfRegistration || false,
                idpEntityId: samlConfig?.idpEntityId || '',
                idpSsoUrl: samlConfig?.idpSsoUrl || '',
                idpSloUrl: samlConfig?.idpSloUrl || '',
                idpX509Cert: '',
                spEntityId: samlConfig?.spEntityId || '',
                spNameIdFormat: samlConfig?.spNameIdFormat || '',
                attributeMapping: samlConfig?.attributeMapping
                    ? JSON.stringify(samlConfig.attributeMapping, null, 2)
                    : '',
                wantAssertionsSigned: samlConfig?.wantAssertionsSigned || false,
                wantMessageSigned: samlConfig?.wantMessageSigned || false,
            });
        }
    }, [samlConfig]);

    const [updateSamlConfig] = useMutation(UPDATE_SAML_CONFIG);
    const handleUpdateSamlConfig = async (formData) => {
        try {
            // Parse attributeMapping if it's a string
            let attributeMapping = formData.attributeMapping;
            if (typeof attributeMapping === 'string' && attributeMapping.trim()) {
                try {
                    attributeMapping = JSON.parse(attributeMapping);
                } catch (e) {
                    form.setError('attributeMapping', { message: t('saml.form.errors.invalidJson') });
                    return;
                }
            } else if (!attributeMapping || attributeMapping.trim() === '') {
                attributeMapping = null;
            }

            await updateSamlConfig({
                variables: {
                    input: {
                        ...formData,
                        attributeMapping,
                    },
                },
            });
            publishToast({ description: t('saml.form.updateSuccess'), status: 'success' });
            form.reset({ ...formData, idpX509Cert: '' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('saml.form.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('saml.title')}</Page.HeaderTitle>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <SettingsFormCard onSubmit={form.handleSubmit(handleUpdateSamlConfig)}>
                        <Controller
                            name="isRequired"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('saml.form.isRequired')}
                                    error={formErrors.isRequired}
                                    helpText={t('saml.form.isRequiredHelpText')}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="domain"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('saml.form.domain')} error={formErrors.domain}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        {/*<Controller
                            name="allowSelfRegistration"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('saml.form.allowSelfRegistration')}
                                    error={formErrors.allowSelfRegistration}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />*/}

                        <div className="text-sm font-semibold mt-4 mb-2">{t('saml.form.idpSettings')}</div>

                        <Controller
                            name="idpEntityId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('saml.form.idpEntityId')} error={formErrors.idpEntityId}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="idpSsoUrl"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('saml.form.idpSsoUrl')} error={formErrors.idpSsoUrl}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="idpSloUrl"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('saml.form.idpSloUrl')} error={formErrors.idpSloUrl}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="idpX509Cert"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('saml.form.idpX509Cert')}
                                    error={formErrors.idpX509Cert}
                                    helpText={t('saml.form.idpX509CertHelpText')}
                                >
                                    <TextField
                                        {...field}
                                        placeholder={t('saml.form.idpX509CertPlaceholder')}
                                        rows={5}
                                    />
                                </FormField>
                            )}
                        />

                        <div className="text-sm font-semibold mt-4 mb-2">{t('saml.form.spSettings')}</div>

                        <Controller
                            name="spEntityId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('saml.form.spEntityId')} error={formErrors.spEntityId}>
                                    <StringField {...field} disabled />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="spNameIdFormat"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('saml.form.spNameIdFormat')} error={formErrors.spNameIdFormat}>
                                    <StringField {...field} disabled />
                                </FormField>
                            )}
                        />

                        {samlConfig?.metadataUrl && (
                            <FormField label={t('saml.form.metadataUrl')} helpText={t('saml.form.metadataUrlHelpText')}>
                                <StringField value={samlConfig.metadataUrl} readOnly disabled />
                            </FormField>
                        )}

                        {samlConfig?.acsUrl && (
                            <FormField label={t('saml.form.acsUrl')} helpText={t('saml.form.acsUrlHelpText')}>
                                <StringField value={samlConfig.acsUrl} readOnly disabled />
                            </FormField>
                        )}

                        <div className="text-sm font-semibold mt-4 mb-2">{t('saml.form.advancedSettings')}</div>

                        <Controller
                            name="wantAssertionsSigned"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('saml.form.wantAssertionsSigned')}
                                    error={formErrors.wantAssertionsSigned}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="wantMessageSigned"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('saml.form.wantMessageSigned')}
                                    error={formErrors.wantMessageSigned}
                                >
                                    <BooleanField {...field} value={field.value ? 'true' : ''} />
                                </FormField>
                            )}
                        />

                        {/*<Controller
                            name="attributeMapping"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('saml.form.attributeMapping')}
                                    error={formErrors.attributeMapping}
                                    helpText={t('saml.form.attributeMappingHelpText')}
                                >
                                    <JsonStringField
                                        {...field}
                                        placeholder={t('saml.form.attributeMappingPlaceholder')}
                                        rows={5}
                                    />
                                </FormField>
                            )}
                        />*/}

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={form.formState.isSubmitting}
                            errorCount={countFieldErrors(formErrors)}
                        />
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default CustomerSamlPage;
